import React, { useMemo, useState } from "react";
import { RevenueThisMonthCard } from "./RevenueThisMonthCard";
import { RevenueThisMonthInsightsPanel } from "./RevenueThisMonthInsightsPanel";
import { useRevenueThisMonth } from "../../hooks/useRevenueThisMonth";
import { useRevenueThisMonthInsights } from "../../hooks/useRevenueThisMonthInsights";
import type { RevenueThisMonthCardProps } from "./RevenueThisMonthCard";
import type { RevenueThisMonthInsightsPanelProps } from "./RevenueThisMonthInsightsPanel";

export interface RevenueThisMonthSectionProps {
  card?: Partial<RevenueThisMonthCardProps>;
  insights?: RevenueThisMonthInsightsPanelProps;
}

const DEFAULT_INSIGHTS: RevenueThisMonthInsightsPanelProps["insights"] = [
  {
    title: "Billing Pace",
    description:
      "Billing pace insights will appear once enough data is available for the selected period.",
    accentColor: "success",
  },
  {
    title: "Invoices Raised",
    description:
      "Invoice insights will appear once enough data is available for the selected period.",
    accentColor: "neutral",
  },
];

export function RevenueThisMonthSection({
  card: cardOverride,
  insights = { monthLabel: "February · Insights", insights: DEFAULT_INSIGHTS },
}: RevenueThisMonthSectionProps) {
  const {
    title,
    subtext,
    amountThisWeek,
    amountThisMonth,
    subTotalThisWeek,
    taxTotalThisWeek,
    subTotalThisMonth,
    taxTotalThisMonth,
    amountLabel,
    badgePrimary,
    weekBadgeLabel,
    showEst,
    loading,
    error,
  } = useRevenueThisMonth();
  const [activePeriod, setActivePeriod] = useState<"month" | "week">("month");

  const insightsData = useRevenueThisMonthInsights({
    enabled: !loading && !error,
  });

  const insightsPanelLoading = loading || insightsData.loading;

  const selectedAmount = useMemo(
    () => (activePeriod === "month" ? amountThisMonth : amountThisWeek),
    [activePeriod, amountThisMonth, amountThisWeek],
  );

  const selectedBreakup = useMemo(() => {
    if (activePeriod === "month") {
      return { subTotal: subTotalThisMonth, taxTotal: taxTotalThisMonth };
    }
    return { subTotal: subTotalThisWeek, taxTotal: taxTotalThisWeek };
  }, [
    activePeriod,
    subTotalThisMonth,
    taxTotalThisMonth,
    subTotalThisWeek,
    taxTotalThisWeek,
  ]);

  const cardProps: RevenueThisMonthCardProps = {
    title,
    subtext,
    amount: selectedAmount,
    subTotal: selectedBreakup.subTotal,
    taxTotal: selectedBreakup.taxTotal,
    amountLabel,
    badgePrimary,
    badgeSecondary: weekBadgeLabel,
    showEst,
    loading,
    activePeriod,
    onPeriodChange: setActivePeriod,
    ...cardOverride,
  };

  return (
    <div className="flex w-full flex-col items-start gap-4 rounded-lg bg-white px-6 py-6 shadow-sm">
      {error && <p className="text-body font-body text-error-600">{error}</p>}
      <div className="flex w-full gap-6 items-stretch">
        <RevenueThisMonthCard {...cardProps} />
        <RevenueThisMonthInsightsPanel
          monthLabel={insightsData.error ? insights.monthLabel : insightsData.monthLabel}
          insights={insightsData.error ? insights.insights : insightsData.insights}
          loading={insightsPanelLoading}
        />
      </div>
    </div>
  );
}
